<template>
	<div class="">
		<div class="bg-ihh-purple py-9 w-full justify-center">
			<div class="flex justify-center">
				<a href="/" class="">
					<CheckoutLogo class="w-full" />
				</a>
			</div>
		</div>

		<Spinner v-if="loading" />

		<div v-else class="max-w-6xl mx-auto">
			<div class="w-full lg:px-8 py-4 md2:pt-0 flex justify-center">
				<div class="flex flex-col items-center mb-12 md2:mx-6">
					<div class="lg:border-b mb-2 lg:mb-6 w-full md2:my-12">
						<div class="text-4xl font-ihh-bold text-ihh-dark-gray text-center">Thank you {{ order?.author?.firstName }}!</div>

						<div class="my-6 mb-3 text-ihh-dark-gray px-8 w-full flex justify-center">
							<div class="max-w-3xl">
								<div>
									Your order number is <strong>{{ order?.bcId }}</strong>
								</div>
								<div class="my-3">
									An email will be sent containing information about your purchase. If you have any questions about your purchase, email us at
									<button @click="openMailApp" class="text-ihh-dark-salmon">Help@inhishands.com</button> or call us at
									<button @click="openDialerApp" class="text-ihh-dark-salmon">800-247-4045</button>
								</div>
							</div>
						</div>
					</div>

					<div class="space-y-2 w-full flex flex-col items-center md2:my-12">
						<button class="w-56 border rounded px-4 py-2 border-ihh-form-label text-ihh-dark-gray flex items-center space-x-2">
							<a href="/">CONTINUE SHOPPING</a>
							<span class="text-2xs">>></span>
						</button>
						<button @click="showSummary = true" class="w-56 border rounded px-4 py-2 border-ihh-form-label text-ihh-dark-gray text-center space-x-2 md2:hidden">
							Order Summary
						</button>
						<a href="#typeform" class="w-56 border rounded px-4 py-2 border-ihh-form-label text-ihh-dark-gray text-center space-x-2 md2:hidden">
							Order Feedback
						</a>
					</div>
				</div>

				<div id="desktopSummary" class="flex-col md:items-center p-2 sm:mx-0 mt-4 hidden md2:block">
					<div class="flex-col flex justify-center items-center h-full w-96 px-4">
						<div
							class="flex-col border border-ihh-gray/10 md3:border-ihh-gray/50 rounded bg-ihh-white shadow-lg w-full max-w-2xl h-full sm:max-h-[500px] md3:max-h-none overflow-scroll md3:h-fit md3:max-w-sm"
						>
							<div class="sticky top-0 relative bg-ihh-white text-ihh-midnight-gray text-xl sm:text-4xl text-center flex w-full justify-center z-10">
								<div class="flex m-auto items-center justify-between w-full md3:border-b border-ihh-slate-gray/20">
									<div class="w-full order-2 md3:order-none">
										<div class="w-full border-b">
											<div class="w-full flex justify-between items-center text-base text-ihh-midnight-gray p-4">
												<div class="font-bold">Order Summary</div>
												<div class="text-xs text-ihh-dark-salmon">
													<button @click="printDetails" class="flex items-center lg:space-x-2 sm:space-x-1">
														<div class="icon">
															<svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
																<path
																	class="fill-ihh-dark-salmon"
																	d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"
																></path>
															</svg>
														</div>
														<span>Print</span>
													</button>
												</div>
											</div>
										</div>
										<OrderSummaryCart
											:showFullCart="showFullCart"
											:order="order"
											:displayed-products="displayedProducts"
											@show-full-cart="showFullCart = $event"
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div id="mobileSummary" v-if="showSummary" class="fixed inset-0 z-50 bg-white overflow-auto md2:hidden text-ihh-midnight-gray">
					<div class="sticky top-0 bg-white border-b px-4 py-6 flex justify-between items-center">
						<div>
							<h2 class="text-lg font-semibold">Order Summary</h2>
							<div class="w-full flex space-x-1">
								<div class="flex justify-start text-start text-base font-normal text-ihh-midnight-gray">
									{{ totalOrderItems }} {{ totalOrderItems === 1 ? 'Item' : 'Items' }}
								</div>
								<div>|</div>
								<div>{{ parseFloat(order?.totalIncTax)?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}</div>
							</div>
						</div>
						<div class="lg:hidden order-4 lg:order-none">
							<button
								@click="showSummary = false"
								class="flex group w-7 h-7 p-2 justify-around z-10 hover:bg-ihh-slate-gray bg-ihh-gray/20 rounded-full font-bold text-xl text-ihh-midnight-gray"
							>
								<div class="m-auto items-center flex w-full justify-center">
									<div class="rotate-45 absolute bg-ihh-midnight-gray group-hover:bg-ihh-white py-[0.5px] rounded-lg px-1"></div>
									<div class="-rotate-45 bg-ihh-midnight-gray group-hover:bg-ihh-white py-[0.5px] rounded-lg px-1"></div>
								</div>
							</button>
						</div>
					</div>
					<div class="flex-col flex justify-center items-center w-full py-4">
						<div class="flex-col w-full max-w-2xl mx-auto">
							<OrderSummaryCart
								:order="order"
								:displayed-products="displayedProducts"
								:showFullCart="showFullCart"
								total-class="text-2xl font-bold"
								@show-full-cart="showFullCart = $event"
							/>
						</div>
					</div>
				</div>
			</div>

			<div id="typeform" class="h-screen" ref="typeformContainer">
				<iframe
					ref="typeformIframe"
					src="https://form.typeform.com/to/RU5C62LQ"
					frameborder="0"
					width="100%"
					height="500px"
					tabindex="-1"
					style="pointer-events: auto"
					@load="handleIframeLoad"
					loading="eager"
					sandbox="allow-scripts allow-same-origin allow-forms"
				></iframe>
			</div>
		</div>
	</div>
</template>

<script>
import CheckoutLogo from '../partials/checkout-logo.vue'
import OrderSummaryCart from '../pages/ihh-items/order-summary-cart.vue'
import Mango from '../../helpers/mango.js'
import axios from 'axios'

export default {
	components: {
		CheckoutLogo,
		OrderSummaryCart,
	},

	inject: ['store'],

	data() {
		return {
			loading: true,
			order: null,
			showSummary: false,
			showFullCart: false,
		}
	},

	methods: {
		openMailApp() {
			let subject
			console.log('Mail app user: ', this.store.user?.id)

			if (this.store?.user?.id) {
				console.log(this.store.user.firstName, this.store.user.lastName)
				subject = `${this.store?.user?.firstName} ${this.store?.user?.lastName} - ${this.store?.user?.email}`
			} else {
				subject = ''
			}

			const mailtoLink = `mailto:help@inhishands.com?subject=${subject}`
			window.location.href = mailtoLink
		},
		openDialerApp() {
			const phoneNumber = '1-800-247-4045'
			const telLink = `tel:${phoneNumber}`
			window.location.href = telLink
		},
		printDetails() {
			this.showSummary = false
			this.showFullCart = true
			setTimeout(() => {
				window.print()
			}, 100)
		},
		handleIframeLoad() {
			// When iframe loads, restore the scroll position to top of page
			this.$nextTick(() => {
				// Force scroll back to top or to a specific element
				window.scrollTo(0, 0)
			})
		},
	},

	async mounted() {
		const bcId = Number(this.$route.params.bcId)
		this.loading = true

		if (!this.store?.checkoutSuccess) {
			// Only load order confirmation if redirected from successful checkout
			this.$router.push('/')
		}

		if (!this.store?.user?.id) {
			this.order = JSON.parse(window.localStorage.getItem('guestOrder'))
			console.log('Guest Order id: ', this.order?.bcId)
		} else {
			console.log('Reading order through sync endpoint...')
			const syncResponse = await axios.get(`${this.store.api}/controllers/bigCommerce/sync/orders/${bcId}`)
			console.log('Sync response: ', syncResponse?.data)
			const ordersResponse = await Mango.orders({ search: { bcId: bcId } })
			console.log('Order id: ', ordersResponse[0]?.id)
			this.order = ordersResponse[0] // test
		}

		this.loading = false

		if (typeof window.gtag === 'function') {
			window.gtag('event', 'conversion', {
				send_to: 'AW-387509916/yWzPCPSGh7EaEJzd47gB',
				value: this.order?.totalIncTax,
				currency: 'USD',
			})
		}

		// Prevent auto-scrolling by setting up a MutationObserver
		this.$nextTick(() => {
			// Create a MutationObserver to watch for changes to the iframe
			const observer = new MutationObserver(() => {
				// Force scroll back to top whenever iframe changes
				window.scrollTo(0, 0)
			})

			// Start observing the iframe for attribute changes
			if (this.$refs.typeformIframe) {
				observer.observe(this.$refs.typeformIframe, {
					attributes: true,
					childList: true,
					subtree: true,
				})

				// Also set up a scroll event handler for the first few seconds
				const scrollHandler = () => {
					window.scrollTo(0, 0)
				}

				window.addEventListener('scroll', scrollHandler)

				// Remove the scroll handler after 3 seconds
				setTimeout(() => {
					window.removeEventListener('scroll', scrollHandler)
					observer.disconnect()
				}, 3000)
			}
		})
	},

	beforeUnmount() {
		delete this.store.checkoutSuccess
		window.localStorage.removeItem('guestOrder')
	},

	computed: {
		totalOrderItems() {
			return (
				this.order?.products?.reduce((total, product) => {
					return total + (product.quantity || product.qty || 0)
				}, 0) || 0
			)
		},
		displayedProducts() {
			return this.order?.products?.slice(0, this.showFullCart ? undefined : 4) || []
		},
	},
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap');

@media print {
	@page {
		size: landscape;
	}

	.bg-ihh-purple,
	button:not(.print-show),
	#typeform,
	.fixed {
		display: none !important;
	}

	#desktopSummary {
		display: block !important;
		width: 100% !important;
		max-width: none !important;
	}

	.w-96 {
		width: 100% !important;
	}

	.overflow-scroll {
		overflow: visible !important;
	}

	.shadow-lg {
		box-shadow: none !important;
	}

	* {
		color: black !important;
		text-align: left !important;
	}
}
</style>
